/**
 * Grant (route) API. Added for the FE↔BE integration (Round 3).
 *
 * A grant activates a canonical route: it says "this connection may serve
 * (product_key, data_type)". The BE enforces:
 *   - data_type must be a current-catalog entity owned by product_key
 *   - the integration must be eligible for product_key
 *   - one active route per (tenant, product_key, data_type) — switching needs replace=true
 *
 * The FE speaks its own product vocabulary, so callers translate via mapBinding
 * (mappers.ts) BEFORE calling here. This module only deals in canonical values.
 */
import { apiFetch, ApiError } from './client';

interface BeGrantResponse {
  grant_id: string;
  product_key: string;
  data_type: string;
  connection_id: string;
  is_active: boolean;
}

/** Raised when the BE reports the single-active-route conflict (409). The store
 *  turns this into the FE's "replace?" confirm flow. */
export class SingleActiveRouteError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SingleActiveRouteError';
  }
}

/**
 * Create (or switch, when replace=true) a grant. Translates the BE's
 * ERR_SINGLE_ACTIVE_ROUTE 409 into a typed error so the caller can prompt the
 * user to confirm a switch rather than silently overwriting an existing owner.
 */
export async function createGrant(
  connectionId: string,
  productKey: string,
  dataType: string,
  replace = false,
): Promise<BeGrantResponse> {
  try {
    return await apiFetch<BeGrantResponse>('/grants', {
      method: 'POST',
      body: { connection_id: connectionId, product_key: productKey, data_type: dataType, replace },
    });
  } catch (err) {
    if (err instanceof ApiError && err.code === 'ERR_SINGLE_ACTIVE_ROUTE') {
      throw new SingleActiveRouteError(err.message);
    }
    throw err;
  }
}

/** Revoke a grant by its grant_id (deactivate-only on the BE). */
export async function revokeGrant(grantId: string): Promise<void> {
  await apiFetch<void>(`/grants/${grantId}`, { method: 'DELETE' });
}

/** List the tenant's active grants (used to resolve grant_ids for revoke). */
export async function listGrants(): Promise<BeGrantResponse[]> {
  const res = await apiFetch<{ grants: BeGrantResponse[] }>('/grants');
  return res.grants;
}
